import axios from 'axios'

const state = {
  adminIds: [],
  departmentIds: [],
  response: null,
  isLoading: false,
  multiMessages: [],
  customerMessage: null,
  customerImageUrl: null,
  assistantFeature: 'chat',
  composerInsert: null,
  // Dedicated vision slice. `response` / `isLoading` are shared with callAiFunctionCalling and
  // `response` is nulled by SET_ASSISTANT_FEATURE, so the v2026 tab must never read them.
  visionRequest: null,
  visionLoading: false,
  visionResult: null,
  visionError: null,
  fcRequest: null,
  fcLoading: false,
  fcResult: null,
  fcError: null,
  fcNotice: null
}

// A reply is only ours if the press that started it is still the current one.
const isCurrentVisionToken = (state, token) =>
  token !== null && !!state.visionRequest && state.visionRequest.token === token

// A null token means the queue was restored from storage with no press behind it, so only a
// still-empty fcRequest may accept that reply.
const isCurrentFcToken = (state, token) =>
  state.fcRequest ? state.fcRequest.token === token : token === null

// Fresh token per notice, so two identical outcomes in a row still fire the panel's watcher.
let fcNoticeToken = 0

const fcShapeOf = (data) => {
  if (!data) return null
  if (data.function_name === 'get_booking_value') {
    return (data.service && data.params) ? 'booking' : null
  }
  if (data.function_name === 'get_tax_invoice') {
    return Array.isArray(data.user_company_addresses) ? 'tax' : null
  }
  if (data.function_name === 'get_order_code') {
    const params = data.params || {}
    return (params.order_code || data.action_url) ? 'order' : null
  }
  return null
}

const actions = {
  fetchAdminIds: async ({ commit }) => {
    try {
      const url = '/ai-assistant/admin-ids'
      const { data } = await axios.get(url)
      if (data && !data.error) {
        commit('SET_ADMIN_IDS', data.admin_ids)
        commit('SET_DEPARTMENT_IDS', data.department_ids)
      }
    } catch (e) {
      console.log('check error', e.message)
    }
  },
  callAiVision: async ({ commit, state }, submitData) => {
    // Captured before the await: a newer press, or a conversation switch (which nulls
    // visionRequest), makes this reply stale and it is dropped.
    const visionToken = state.visionRequest ? state.visionRequest.token : null
    try {
      commit('SET_RESPONSE', null)
      commit('SET_IS_LOADING', true)
      if (visionToken !== null) {
        commit('SET_VISION_LOADING', true)
        commit('SET_VISION_RESULT', null)
        commit('SET_VISION_ERROR', null)
      }
      const url = '/ai-assistant/ai-vision'
      const { data } = await axios.post(url, submitData)
      commit('SET_IS_LOADING', false)
      if (!data.error) {
        commit('SET_RESPONSE', data)
      }
      if (!isCurrentVisionToken(state, visionToken)) return
      commit('SET_VISION_LOADING', false)
      if (data && !data.error) {
        commit('SET_VISION_RESULT', data)
      } else {
        commit('SET_VISION_ERROR', 'failed')
      }
    } catch (e) {
      console.log('check error', e.message)
      commit('SET_IS_LOADING', false)
      if (!isCurrentVisionToken(state, visionToken)) return
      commit('SET_VISION_LOADING', false)
      commit('SET_VISION_ERROR', 'failed')
    }
  },
  callAiFunctionCalling: async ({ commit }, submitData) => {
    try {
      commit('SET_RESPONSE', null)
      commit('SET_IS_LOADING', true)
      const url = '/ai-assistant/function-calling'
      const { data } = await axios.post(url, submitData)
      commit('SET_IS_LOADING', false)
      if (!data.error) {
        commit('SET_RESPONSE', data)
      }
    } catch (e) {
      console.log('check error', e.message)
      commit('SET_IS_LOADING', false)
    }
  },
  callAiFcExtract: async ({ commit, state }, submitData) => {
    // Captured before the await: a newer press makes this reply stale and it is dropped.
    const fcToken = state.fcRequest ? state.fcRequest.token : null
    try {
      commit('SET_FC_LOADING', true)
      commit('SET_FC_RESULT', null)
      commit('SET_FC_ERROR', null)
      const url = '/ai-assistant/function-calling'
      const { data } = await axios.post(url, submitData)
      if (!isCurrentFcToken(state, fcToken)) return
      commit('SET_FC_LOADING', false)
      if (data && data.error) {
        commit('SET_FC_ERROR', 'server')
        commit('SET_FC_NOTICE', { kind: 'server' })
        return
      }
      if (!data || !data.function_name) {
        commit('SET_FC_ERROR', 'empty')
        commit('SET_FC_NOTICE', { kind: 'empty' })
        return
      }
      // Written even when unreadable, so the card can print the function name back.
      commit('SET_FC_RESULT', data)
      const shape = fcShapeOf(data)
      if (!shape) {
        commit('SET_FC_ERROR', 'unreadable')
        commit('SET_FC_NOTICE', { kind: 'unreadable', functionName: data.function_name })
        return
      }
      commit('SET_FC_NOTICE', {
        kind: 'success',
        shape: shape,
        orderCode: (data.params && data.params.order_code) || null
      })
    } catch (e) {
      console.log('check error', e.message)
      if (!isCurrentFcToken(state, fcToken)) return
      commit('SET_FC_LOADING', false)
      commit('SET_FC_ERROR', 'network')
      commit('SET_FC_NOTICE', { kind: 'network' })
    }
  }
}

const mutations = {
  SET_IS_LOADING: (state, isLoading) => {
    state.isLoading = isLoading
  },
  SET_RESPONSE: (state, response) => {
    state.response = response
  },
  SET_CUSTOMER_MESSAGE: (state, customerMessage) => {
    state.multiMessages = []
    state.customerMessage = customerMessage
  },
  SET_REPLY_TRANSLATE_AI: (state, replyTranslateAi) => {
    state.multiMessages = []
    state.replyTranslateAi = replyTranslateAi
  },
  // Declared key, unlike replyTranslateAi above. Payload is a fresh { text, token } object every
  // time, so sending the same translation twice still fires the consumer's watcher.
  SET_COMPOSER_INSERT: (state, payload) => {
    state.composerInsert = payload
  },
  SET_CUSTOMER_IMAGE_URL: (state, customerImageUrl) => {
    state.customerImageUrl = customerImageUrl
  },
  SET_ADMIN_IDS: (state, adminIds) => {
    state.adminIds = adminIds
  },
  SET_DEPARTMENT_IDS: (state, departmentIds) => {
    state.departmentIds = departmentIds
  },
  ADD_MULTI_MESSAGES: (state, message) => {
    state.customerMessage = null
    state.multiMessages.push(message)
  },
  REMOVE_ONE_OF_MULTI_MESSAGES: (state, key) => {
    state.multiMessages.splice(key, 1)
  },
  CLEAR_MULTI_MESSAGES: (state) => {
    state.multiMessages = []
  },
  SET_ASSISTANT_FEATURE: (state, featureName) => {
    state.response = null
    state.assistantFeature = featureName
  },
  // Fresh { token, photoUrl, submitData } object on every press, so pressing the same image twice
  // still fires the consumers' watchers. Doubles as the retry payload.
  SET_VISION_REQUEST: (state, payload) => {
    state.visionRequest = payload
  },
  SET_VISION_LOADING: (state, visionLoading) => {
    state.visionLoading = visionLoading
  },
  SET_VISION_RESULT: (state, visionResult) => {
    state.visionResult = visionResult
  },
  SET_VISION_ERROR: (state, visionError) => {
    state.visionError = visionError
  },
  // Leaves response / isLoading / assistantFeature alone: those belong to the legacy path.
  CLEAR_VISION: (state) => {
    state.visionRequest = null
    state.visionLoading = false
    state.visionResult = null
    state.visionError = null
  },
  SET_FC_REQUEST: (state, payload) => {
    state.fcRequest = payload
    state.fcLoading = false
    state.fcResult = null
    state.fcError = null
  },
  SET_FC_LOADING: (state, fcLoading) => {
    state.fcLoading = fcLoading
  },
  SET_FC_RESULT: (state, fcResult) => {
    state.fcResult = fcResult
  },
  SET_FC_ERROR: (state, fcError) => {
    state.fcError = fcError
  },
  SET_FC_NOTICE: (state, payload) => {
    state.fcNotice = payload ? Object.assign({}, payload, { token: ++fcNoticeToken }) : null
  },
  CLEAR_FC: (state) => {
    state.fcRequest = null
    state.fcLoading = false
    state.fcResult = null
    state.fcError = null
    state.fcNotice = null
  }
}

const getters = {}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
